import Image from "next/image";
import {
  LabelBlock,
  SubLabel,
  LabelValue,
  Root,
  SlotBody,
  SlotHeader,
  SlotHeaderData,
  SlotHeaderH1,
  SlotHeaderImage,
  SlotMain,
  SlotMeta,
  SlotMetaHeader,
  SlotMetaItem,
  SlotMetaKey,
  SlotMetaMain,
  SlotMetaValue,
  SlotMetaWrapper,
  MainImage,
  SlotMetaIcon,
  ProviderName,
  ProviderBlock,
  TopImage,
} from "./styles";
import * as luxon from "luxon";
import { SlotType } from "../utils/types/Slot";
import SlotDemo from "./components/SlotDemo";

const PublicSlot: React.FC<{ slot: SlotType }> = ({ slot }) => {
  return (
    <Root>
      <SlotBody>
        <SlotHeader>
          <SlotHeaderImage>
            <MainImage
              src={slot.imageUrl}
              alt="slot-img"
              width={436}
              height={253}
              style={{
                borderRadius: 4,
              }}
            />
          </SlotHeaderImage>
          <SlotHeaderData>
            <SlotHeaderH1>{slot.title}</SlotHeaderH1>
            {slot.provider && (
              <LabelBlock>
                <SubLabel>provider</SubLabel>
                <ProviderBlock>
                  <ProviderName>{slot.provider.name}</ProviderName>
                  <Image src={slot.provider.imageUrl} alt="provider-img" height={52} width={128} />
                </ProviderBlock>
              </LabelBlock>
            )}
            {slot.adminRate && (
              <LabelBlock>
                <SubLabel>rating</SubLabel>
                <LabelValue>
                  {Array.from({ length: slot.adminRate ?? 0 }).map((_, index) => (
                    <Image
                      key={index}
                      src="/images/icons/gold-star.png"
                      alt="star"
                      width={16}
                      height={16}
                    />
                  ))}
                  {Array.from({ length: 5 - (slot.adminRate ?? 0) }).map((_, index) => (
                    <Image
                      key={index}
                      src="/images/icons/dark-star.png"
                      alt="star"
                      width={16}
                      height={16}
                    />
                  ))}
                </LabelValue>
              </LabelBlock>
            )}
          </SlotHeaderData>
        </SlotHeader>
        <SlotMain>
          {slot.demoUrl ? <SlotDemo demoUrl={slot.demoUrl} imageUrl={slot.imageUrl} /> : <></>}
          <div dangerouslySetInnerHTML={{ __html: slot.body }}></div>
        </SlotMain>
      </SlotBody>
      {(slot.gameUrl ||
        slot.rtp ||
        slot.volatility ||
        slot.maxPotential ||
        slot.minBet ||
        slot.maxBet ||
        slot.paylines ||
        slot.reels ||
        slot.rows ||
        slot.released) && (
        <SlotMetaWrapper>
          {(slot.rtp ||
            slot.volatility ||
            slot.maxPotential ||
            slot.minBet ||
            slot.maxBet ||
            slot.paylines ||
            slot.reels ||
            slot.rows ||
            slot.released) && (
            <SlotMeta>
              <SlotMetaHeader>slot info</SlotMetaHeader>
              <SlotMetaMain>
                {slot.rtp && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon src="/images/icons/rtp.png" alt="rtp" width={16} height={16} />
                      rtp
                    </SlotMetaKey>
                    <SlotMetaValue>{slot.rtp} %</SlotMetaValue>
                  </SlotMetaItem>
                )}
                {slot.volatility && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon
                        src="/images/icons/volatility.png"
                        alt="volatility"
                        width={16}
                        height={16}
                      />
                      volatility
                    </SlotMetaKey>
                    <SlotMetaValue>{slot.volatility}</SlotMetaValue>
                  </SlotMetaItem>
                )}
                {slot.maxPotential && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon
                        src="/images/icons/star.png"
                        alt="maxPotential"
                        width={16}
                        height={16}
                      />
                      max potential
                    </SlotMetaKey>
                    <SlotMetaValue>{slot.maxPotential}X</SlotMetaValue>
                  </SlotMetaItem>
                )}
                {(slot.minBet || slot.maxBet) && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon
                        src="/images/icons/dollar.png"
                        alt="min/max bet"
                        width={16}
                        height={16}
                      />
                      min/max bet
                    </SlotMetaKey>
                    <SlotMetaValue>
                      {slot.minBet ?? "?"}/{slot.maxBet ?? "?"}
                    </SlotMetaValue>
                  </SlotMetaItem>
                )}
                {slot.paylines && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon
                        src="/images/icons/paylines.png"
                        alt="paylines"
                        width={16}
                        height={16}
                      />
                      pay lines
                    </SlotMetaKey>
                    <SlotMetaValue>{slot.paylines}</SlotMetaValue>
                  </SlotMetaItem>
                )}
                {slot.reels && slot.rows && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon
                        src="/images/icons/reels.png"
                        alt="reels"
                        width={16}
                        height={16}
                      />
                      reels
                    </SlotMetaKey>
                    <SlotMetaValue>
                      {slot.reels}x{slot.rows}
                    </SlotMetaValue>
                  </SlotMetaItem>
                )}
                {slot.released && (
                  <SlotMetaItem>
                    <SlotMetaKey>
                      <SlotMetaIcon
                        src="/images/icons/released.png"
                        alt="released"
                        width={16}
                        height={16}
                      />
                      released
                    </SlotMetaKey>
                    <SlotMetaValue>
                      {luxon.DateTime.fromISO(slot.released).toFormat("d LLL yyyy")}
                    </SlotMetaValue>
                  </SlotMetaItem>
                )}
              </SlotMetaMain>
            </SlotMeta>
          )}
          {/* {slot.gameUrl && (
            <Link href={slot.gameUrl} target="_blank">
              <Button>
                <ButtonBlue>Play game</ButtonBlue>
              </Button>
            </Link>
          )} */}
        </SlotMetaWrapper>
      )}
      <TopImage src={slot.imageUrl} alt="slot-img" width={436} height={253} />
    </Root>
  );
};

export default PublicSlot;
